import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Rocket, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHead } from "@/components/brand/Card";
import { Pill, type PillTone } from "@/components/brand/Pill";
import { ConfirmDialog } from "@/components/admin/reference-data/dialogs";
import { Empty, ErrorBlock, Loading, StaleNotice } from "@/components/admin/reference-data/shared";
import { useConfigPackages, usePromoteConfigPackage, useRollbackConfigPackage } from "@/hooks/useApi";
import { isInitialLoad, packageReleaseLabel } from "@/lib/reference-data-config-types";
import { errMessage } from "@/lib/reference-data-edit";
import { formatRelative } from "@/lib/datetime";
import type { ConfigPackage, ConfigPackageState, ConfigTypeSummary } from "@/types/reference-data";

const STATE_TONE: Record<ConfigPackageState, PillTone> = {
  running: "muted",
  failed: "danger",
  staged: "gold",
  active: "ok",
  superseded: "muted",
};

/** Pacotes do tipo: promote do rascunho e rollback para um pacote já promovido. */
export function PackageReleasesPanel({
  configType,
  labelOf,
}: {
  configType: ConfigTypeSummary;
  labelOf: (table: string) => string;
}) {
  const { t } = useTranslation();
  const tables = configType.tables.map((x) => x.table_name);
  const { data, isLoading, isError, refetch } = useConfigPackages(configType.key);
  const promote = usePromoteConfigPackage(configType.key, tables);
  const rollback = useRollbackConfigPackage(configType.key, tables);
  const [target, setTarget] = useState<{ pkg: ConfigPackage; action: "promote" | "rollback" } | null>(null);
  const items = data?.items ?? [];
  const stateLabel: Record<ConfigPackageState, string> = {
    running: t("Running"),
    failed: t("Failed"),
    staged: t("Draft"),
    active: t("Active"),
    superseded: t("previous"),
  };

  const confirm = async () => {
    if (!target) return;
    const mutation = target.action === "promote" ? promote : rollback;
    try {
      await mutation.mutateAsync(target.pkg.package_id);
      toast.success(
        target.action === "promote"
          ? t("Release {{release}} promoted in every table.", { release: packageReleaseLabel(target.pkg) })
          : t("Every table is back to release {{release}}.", { release: packageReleaseLabel(target.pkg) }),
      );
    } catch (err) {
      toast.error(errMessage(err, target.action === "promote" ? t("Promotion failed.") : t("Rollback failed.")));
    } finally {
      setTarget(null);
    }
  };

  return (
    <Card>
      <CardHead overline={t("ONE PACKAGE PER UPLOAD")} title={t("Releases")} />
      <div className="p-2">
        {isLoading ? (
          <Loading />
        ) : isError && !data ? (
          <ErrorBlock message={t("Could not load the releases.")} onRetry={() => void refetch()} />
        ) : items.length === 0 ? (
          <Empty text={t("No releases yet.")} />
        ) : (
          <div className="divide-y divide-[#f2f4f7]">
            {isError && <StaleNotice />}
            {items.map((pkg) => (
              <div key={pkg.package_id} className="flex items-center gap-3 px-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-brand font-bold text-[14px] text-[#08254c]">{packageReleaseLabel(pkg)}</span>
                    <Pill tone={STATE_TONE[pkg.state]}>{stateLabel[pkg.state]}</Pill>
                    {pkg.legacy && <span className="mono muted text-[10px] uppercase">{t("Legacy")}</span>}
                    {isInitialLoad(pkg) && (
                      <span className="mono muted text-[10px] uppercase">{t("Initial load")}</span>
                    )}
                    {pkg.state === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#7b8799]" />}
                  </div>
                  <div className="mono muted text-[11px] mt-0.5">
                    {formatRelative(pkg.created_at)} ·{" "}
                    {pkg.runs.map((r) => `${labelOf(r.table_name)} ${r.version ?? "—"}`).join(" · ")}
                  </div>
                  {pkg.state === "failed" && pkg.failure_reason && (
                    <div className="text-[11px] text-[#b02a31] font-mono mt-1">{pkg.failure_reason}</div>
                  )}
                </div>
                {pkg.state === "staged" && (
                  <Button variant="action" size="sm" className="gap-1.5" onClick={() => setTarget({ pkg, action: "promote" })}>
                    <Rocket className="w-3.5 h-3.5" /> {t("Promote {{release}}", { release: packageReleaseLabel(pkg) })}
                  </Button>
                )}
                {pkg.state === "superseded" && pkg.promoted_at && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setTarget({ pkg, action: "rollback" })}>
                    <Undo2 className="w-3.5 h-3.5" /> {t("Roll back to {{release}}", { release: packageReleaseLabel(pkg) })}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!target}
        onOpenChange={(o) => !o && setTarget(null)}
        title={target?.action === "rollback" ? t("Roll back release") : t("Promote release")}
        description={
          target?.action === "rollback"
            ? t("Every table of the configuration type goes back to this release. The agents read it immediately.")
            : t("Promote this release in every table of the configuration type? The agents read it immediately.")
        }
        confirmLabel={target?.action === "rollback" ? t("Roll back") : t("Promote")}
        confirmIcon={target?.action === "rollback" ? <Undo2 className="w-3.5 h-3.5" /> : <Rocket className="w-3.5 h-3.5" />}
        onConfirm={confirm}
        loading={promote.isPending || rollback.isPending}
      />
    </Card>
  );
}
